import React, { useCallback } from 'react'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import Image from 'components/Common/Image'
import Clickable from 'components/Common/Clickable'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CheckboxButton from 'components/Luxkit/Checkbox/CheckboxButton'

const StaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: ${rem(16)};
  grid-row-gap: ${rem(24)};
  justify-content: space-between;

  ${mediaQueryUp.tablet} {
    grid-column-gap: ${rem(12)};
    grid-template-columns: repeat(3, 1fr);
  }
  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const Item = styled(Clickable)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const ImageContainer = styled.div`
  border: 2px solid transparent;

  &.selected {
    border-color: ${props => props.theme.palette.neutral.default.one};
  }
`

const FloatingCheckbox = styled(CheckboxButton)`
  position: absolute;
  top: ${rem(8)};
  right: ${rem(8)};
`

const isDestinationItem = (item: App.UserPreferencesItem | App.UserPreferencesDestinationItem): item is App.UserPreferencesDestinationItem => {
  return 'destination' in item
}

interface Props {
  showLabel?: boolean;
  items: Array<App.UserPreferencesItem | App.UserPreferencesDestinationItem>;
  onSelect: (item: string | App.UserPreferencesDestination) => void;
  isSelected: (item: string | App.UserPreferencesDestination) => boolean;
}

function TravelPreferencesImageItemsGrid(props: Props) {
  const { showLabel = true, items, onSelect, isSelected } = props

  const onToggleSelection = useCallback((item: App.UserPreferencesItem | App.UserPreferencesDestinationItem) => {
    if (isDestinationItem(item)) {
      return onSelect(item.destination)
    } else {
      return onSelect(item.name)
    }
  }, [onSelect])

  return (
    <StaysGrid>
      {items.map((item, index) => (
        <Item key={isDestinationItem(item) ? item.destination.placeId : index} onClick={() => onToggleSelection(item)}>
          <ImageContainer className={cn({ selected: isDestinationItem(item) ? isSelected(item.destination) : isSelected(item.name) })}>
            <Image fit="center" id={item.image} alt={isDestinationItem(item) ? item.destination.name : item.name} width={192} height={124} />
          </ImageContainer>
          {showLabel && <BodyText colour="neutral-two" weight="bold" variant="medium">{isDestinationItem(item) ? item.destination.name : item.name}</BodyText>}
          {isSelected(isDestinationItem(item) ? item.destination : item.name) && <FloatingCheckbox size="large" checked />}
        </Item>
      ))}
    </StaysGrid>
  )
}

export default TravelPreferencesImageItemsGrid
